import * as mimeTypes from 'mime/lite';
import mergeImages from 'merge-images-v2';

export const TEN_MEGABYTES = 10 * 1024 * 1024;

export const blobToBase64DataUrl = (blob: Blob): Promise<any> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => {
            console.log('Error coverting blob to base 64: ', reader.error);
            reject(reader.error);
        };
        reader.onload = () => {
            resolve(reader.result);
        };
        reader.readAsDataURL(blob);
    });

/**
 * Creates a base 64 attachment string for the email composer plugin
 *
 * @param b64
 * @param fileName Including file extension
 */
export const base64ToEmailComposerBase64Format = (
    b64: string,
    fileName: string
): string => {
    if (!fileName) {
        throw new Error('File name is required for email composer attachment');
    }

    // eslint-disable-next-line prefer-const
    let [_, base64Str] = b64.split(';');
    base64Str = base64Str.substring('base64,'.length);
    const attachment = `base64:${fileName}//${base64Str}`;
    return attachment;
};

export const base64ToDataUrl = (
    b64: string,
    options: { ext?: string; mimeType?: string }
): string => {
    const mime = options.ext ? getMimeFromFileExt(options.ext) : options.mimeType;

    if (!mime) {
        throw new Error('Mime type is required to build data URL');
    }

    const dataUrl = `data:${mime};base64,${b64}`;
    return dataUrl;
};

/**
 * Extract the file extension embedded in the data url string
 */
export const getFileExtFromDataUrl = (b64: string): string | null => {
    // eslint-disable-next-line prefer-const
    let [dataType, _] = b64.split(';');
    const mime = dataType.replace('data:', '');
    return fileExtFromMime(mime);
};

/**
 * Pass in a file extension and get the corresponding mime type
 */
// eslint-disable-next-line arrow-body-style
export const getMimeFromFileExt = (ext: string) => {
    return mimeTypes.getType(ext);
};

/**
 * Pass in a mime type and get the corresponding file extension
 */
export const fileExtFromMime = (mime: string): string | null =>
    mimeTypes.getExtension(mime);

/**
 * Merge multiple images into one
 */
export const combineImages = (images: string[]): Promise<string> =>  mergeImages([...images]);

/**
 * Signatures (base64-encoded magic bytes) for common image formats. CDN
 * "transform" URLs can't be trusted to reflect the actual returned bytes;
 * e.g. a `-svg` URL segment may still be rasterized to PNG when width/height
 * transform params are applied. Sniffing the real bytes avoids mislabeling
 * the mime type and producing an unrenderable data URL.
 */
const BASE64_IMAGE_SIGNATURES: { prefix: string; mimeType: string; ext: string }[] = [
    { prefix: 'iVBORw0KGgo', mimeType: 'image/png', ext: 'png' },
    { prefix: '/9j/', mimeType: 'image/jpeg', ext: 'jpg' },
    { prefix: 'R0lGOD', mimeType: 'image/gif', ext: 'gif' },
    { prefix: 'UklGR', mimeType: 'image/webp', ext: 'webp' },
    { prefix: 'PHN2Zy', mimeType: 'image/svg+xml', ext: 'svg' }, // <svg
    { prefix: 'PD94bWwg', mimeType: 'image/svg+xml', ext: 'svg' }, // <?xml
];

/**
 * Detects the real image mime type/extension from base64-encoded file
 * contents by checking the file's magic-byte signature, rather than
 * trusting a file name or URL, which may not match the actual bytes.
 */
export const detectImageTypeFromBase64 = (
    base64: string
): { mimeType: string; ext: string } | null => {
    const match = BASE64_IMAGE_SIGNATURES.find(({ prefix }) =>
        base64.startsWith(prefix)
    );

    return match ? { mimeType: match.mimeType, ext: match.ext } : null;
};

